import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getFirestore,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  limit,
  orderBy,
  where,
} from "firebase/firestore";
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";

const MakaleDetay = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageUrl, setImageUrl] = useState("/images/placeholder.jpg");
  const [similarArticles, setSimilarArticles] = useState([]);
  const [projects, setProjects] = useState([]);
  const [latestNews, setLatestNews] = useState([]);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const db = getFirestore();
        const storage = getStorage();

        const articlesRef = collection(db, "articles");
        const docRef = doc(articlesRef, id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setArticle(data);

          if (data.image_url) {
            try {
              const storageRef = ref(storage, data.image_url);
              const url = await getDownloadURL(storageRef);
              setImageUrl(url);
            } catch (error) {
              console.warn("Görsel yüklenemedi:", error.message);
              setImageUrl("/images/placeholder.jpg");
            }
          } else {
            setImageUrl("/images/placeholder.jpg");
          }
        } else {
          setArticle(null);
        }
      } catch (error) {
        console.error("Makale alınırken hata:", error);
        setArticle(null);
      } finally {
        setLoading(false);
      }
    };

    const fetchSimilarArticles = async () => {
      try {
        const db = getFirestore();
        const articlesRef = collection(db, "articles");
        const q = query(articlesRef, orderBy("timestamp", "desc"), limit(6));
        const snapshot = await getDocs(q);
        const articles = snapshot.docs
          .filter((doc) => doc.id !== id)
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
        setSimilarArticles(articles);
      } catch (error) {
        console.error("Benzer makaleler yüklenirken hata:", error);
      }
    };

    const fetchProjects = async () => {
      try {
        const db = getFirestore();
        const projectsRef = collection(db, "projects");
        const q = query(projectsRef, limit(6));
        const snapshot = await getDocs(q);
        const projects = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setProjects(projects);
      } catch (error) {
        console.error("Projeler yüklenirken hata:", error);
      }
    };

    const fetchLatestNews = async () => {
      try {
        const db = getFirestore();
        const newsRef = collection(db, "news");
        const q = query(newsRef, orderBy("created_at", "desc"), limit(3));
        const snapshot = await getDocs(q);
        const news = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setLatestNews(news);
      } catch (error) {
        console.error("Haberler yüklenirken hata:", error);
      }
    };

    fetchArticle();
    fetchSimilarArticles();
    fetchProjects();
    fetchLatestNews();
  }, [id]);

  const formatPrice = (price) => {
    if (!price) return "Fiyat bilgisi yok";
    if (typeof price === "string") {
      if (price.includes("TL") || price.includes("₺")) return price;
      const numericPrice = price.replace(/[^\d]/g, "");
      if (numericPrice)
        return `${parseInt(numericPrice).toLocaleString("tr-TR")} TL`;
    }
    if (typeof price === "number")
      return `${price.toLocaleString("tr-TR")} TL`;
    return price;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "Tarih belirtilmemiş";
    
    try {
      if (timestamp && timestamp.toDate) {
        const date = timestamp.toDate();
        return date.toLocaleDateString("tr-TR", {
          day: "numeric",
          month: "long",
          year: "numeric",
        });
      } else if (timestamp && typeof timestamp === "object" && timestamp.seconds) {
        const date = new Date(timestamp.seconds * 1000);
        return date.toLocaleDateString("tr-TR", {
          day: "numeric",
          month: "long",
          year: "numeric",
        });
      } else if (timestamp) {
        const date = new Date(timestamp);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString("tr-TR", {
            day: "numeric",
            month: "long",
            year: "numeric",
          });
        }
      }
    } catch (error) {
      console.warn("Tarih formatlanırken hata:", error);
    }
    
    return "Tarih belirtilmemiş";
  };

  const formatNewsDate = (timestamp) => {
    if (!timestamp) return "";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("tr-TR", {
      day: "numeric",
      month: "short",
    });
  };

  const getExcerpt = (content, maxLength = 160) => {
    if (!content) return "";
    const plainText = content.replace(/[#*`>_-]/g, "").replace(/\s+/g, " ").trim();
    if (plainText.length <= maxLength) return plainText;
    return plainText.substring(0, maxLength).trim() + "...";
  };

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.spinner} />
        <p style={{ color: "#666", marginTop: "16px" }}>Yükleniyor...</p>
      </div>
    );
  }

  if (!article) {
    return (
      <div style={styles.error}>
        <p>Makale bulunamadı.</p>
        <button 
          onClick={() => navigate("/articles")}
          style={styles.backButton}
        >
          Makalelere Dön
        </button>
      </div>
    );
  }

  return (
    <div style={styles.page}>

      {/* ── HERO BANNER ── */}
      <div style={styles.hero}>
        <img
          src={imageUrl}
          alt={article.title || "Makale Görseli"}
          style={styles.heroImage}
        />
        <div style={styles.heroOverlay} />
        <div style={styles.heroContent}>
          <span style={styles.categoryBadge}>Makale</span>
          <h1 style={styles.heroTitle}>{article.title}</h1>
          <div style={styles.heroMeta}>
            {article.author && (
              <span style={styles.metaItem}>
                <svg style={styles.metaIcon} viewBox="0 0 24 24" fill="none">
                  <path
                    d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <circle
                    cx="12"
                    cy="7"
                    r="4"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                </svg>
                {article.author}
              </span>
            )}
            <span style={styles.metaItem}>
              <svg style={styles.metaIcon} viewBox="0 0 24 24" fill="none">
                <rect
                  x="3"
                  y="4"
                  width="18"
                  height="18"
                  rx="2"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path
                  d="M16 2v4M8 2v4M3 10h18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              {formatDate(article.timestamp || article.created_at || article.date)}
            </span>
          </div>
        </div>
      </div>

      {/* ── İÇERİK ── */}
      <div style={styles.contentWrapper}>

        {/* Özet / Lead paragraph */}
        {article.summary && (
          <p style={styles.leadText}>{article.summary}</p>
        )}

        <div style={styles.divider} />

        <ReactMarkdown
          rehypePlugins={[rehypeRaw]}
          children={article.content || ""}
          components={{
            p: ({ node, ...props }) => (
              <p style={styles.bodyText} {...props} />
            ),
            strong: ({ node, ...props }) => (
              <strong style={{ fontWeight: "700", color: "#1a1a1a" }} {...props} />
            ),
            h1: ({ node, ...props }) => (
              <h1 style={styles.inlineH1} {...props} />
            ),
            h2: ({ node, ...props }) => (
              <h2 style={styles.inlineH2} {...props} />
            ),
            h3: ({ node, ...props }) => (
              <h3 style={styles.inlineH3} {...props} />
            ),
            h4: ({ node, ...props }) => (
              <h4 style={styles.inlineH4} {...props} />
            ),
            blockquote: ({ node, ...props }) => (
              <blockquote style={styles.blockquote} {...props} />
            ),
            ul: ({ node, ...props }) => (
              <ul style={styles.list} {...props} />
            ),
            ol: ({ node, ...props }) => (
              <ol style={styles.list} {...props} />
            ),
            li: ({ node, ...props }) => (
              <li style={styles.listItem} {...props} />
            ),
            a: ({ node, ...props }) => (
              <a style={styles.link} target="_blank" rel="noopener noreferrer" {...props} />
            ),
            img: ({ node, ...props }) => (
              <img style={styles.contentImage} {...props} alt={props.alt || "Makale görseli"} />
            ),
          }}
        />
      </div>

      {/* ── SON DAKİKA HABERLER ── */}
      {latestNews.length > 0 && (
        <div style={styles.newsSection}>
          <div style={styles.sectionInner}>
            <div style={styles.sectionHeader}>
              <div style={styles.headerIconWrapper}>
                <svg style={styles.headerIcon} viewBox="0 0 24 24" fill="none">
                  <path
                    d="M22 12h-4l-3 9-4-18-3 9H2"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3 style={styles.sectionTitle}>Son Dakika Haberler</h3>
              <div style={styles.titleUnderline} />
            </div>
            <div style={styles.newsList}>
              {latestNews.map((news, index) => (
                <div
                  key={news.id}
                  style={styles.newsItem}
                  onClick={() => navigate(`/news/${news.id}`)}
                >
                  <div style={styles.newsNumber}>{String(index + 1).padStart(2, '0')}</div>
                  <div style={styles.newsContent}>
                    <h4 style={styles.newsItemTitle}>{news.title}</h4>
                    <div style={styles.newsItemMeta}>
                      <span style={styles.newsItemDate}>
                        {formatNewsDate(news.created_at || news.date)}
                      </span>
                      {news.category && (
                        <span style={styles.newsItemCategory}>{news.category}</span>
                      )}
                    </div>
                  </div>
                  <svg style={styles.newsArrow} viewBox="0 0 24 24" fill="none">
                    <path
                      d="M5 12h14M12 5l7 7-7 7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── BENZER MAKALELER ── */}
      {similarArticles.length > 0 && (
        <div style={styles.articlesSection}>
          <div style={styles.sectionInner}>
            <div style={styles.sectionHeader}>
              <h3 style={styles.sectionTitle}>Benzer Makaleler</h3>
              <div style={styles.titleUnderline} />
            </div>
            <div style={styles.similarGrid}>
              {similarArticles.map((item) => (
                <div
                  key={item.id}
                  style={styles.similarCard}
                  onClick={() => navigate(`/article/${item.id}`)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-5px)";
                    e.currentTarget.style.boxShadow = "0 12px 28px rgba(0,0,0,0.15)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 2px 10px rgba(0,0,0,0.08)";
                  }}
                >
                  <div style={styles.cardImageWrapper}>
                    <img
                      src={
                        item.image_url ||
                        "https://via.placeholder.com/400x220?text=Görsel+Yok"
                      }
                      alt={item.title}
                      style={styles.cardImage}
                      onError={(e) => {
                        e.currentTarget.src = "/images/placeholder.jpg";
                      }}
                    />
                    <span style={styles.cardBadge}>Makale</span>
                  </div>
                  <div style={styles.cardBody}>
                    <h4 style={styles.cardTitle}>{item.title}</h4>
                    <div style={styles.cardMeta}>
                      <svg
                        style={styles.cardMetaIcon}
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <rect
                          x="3"
                          y="4"
                          width="18"
                          height="18"
                          rx="2"
                          stroke="currentColor"
                          strokeWidth="2"
                        />
                        <path
                          d="M16 2v4M8 2v4M3 10h18"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                      <span style={styles.cardDate}>
                        {formatDate(item.timestamp || item.created_at || item.date)}
                      </span>
                    </div>
                    <div style={styles.cardFooter}>
                      <span style={styles.cardExcerpt}>
                        {getExcerpt(item.content, 80)}
                      </span>
                      <span style={styles.cardCta}>Devamını Oku →</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── PROJELER ── */}
      {projects.length > 0 && (
        <div style={styles.projectsSection}>
          <div style={styles.sectionInner}>
            <div style={styles.sectionHeader}>
              <h3 style={styles.sectionTitle}>Projelere Göz At</h3>
              <div style={styles.titleUnderline} />
            </div>
            <div style={styles.projectsGrid}>
              {projects.map((project) => (
                <div
                  key={project.id}
                  style={styles.projectCard}
                  onClick={() => navigate(`/project/${project.id}`)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-5px)";
                    e.currentTarget.style.boxShadow = "0 12px 28px rgba(0,0,0,0.15)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 2px 10px rgba(0,0,0,0.08)";
                  }}
                >
                  <div style={styles.cardImageWrapper}>
                    <img
                      src={
                        project.image_urls?.[0] ||
                        "https://via.placeholder.com/400x220?text=Görsel+Yok"
                      }
                      alt={project.project_name}
                      style={styles.cardImage}
                    />
                    {project.project_type && (
                      <span style={styles.cardBadge}>
                        {project.project_type}
                      </span>
                    )}
                  </div>
                  <div style={styles.cardBody}>
                    <h4 style={styles.cardTitle}>{project.project_name}</h4>
                    <div style={styles.cardMeta}>
                      <svg
                        style={styles.cardMetaIcon}
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
                          stroke="currentColor"
                          strokeWidth="2"
                        />
                        <circle
                          cx="12"
                          cy="9"
                          r="2.5"
                          stroke="currentColor"
                          strokeWidth="2"
                        />
                      </svg>
                      <span style={styles.cardLocation}>
                        {[project.district, project.city]
                          .filter(Boolean)
                          .join(", ") || "Konum belirtilmemiş"}
                      </span>
                    </div>
                    <div style={styles.projectFooter}>
                      <span style={styles.cardPrice}>
                        {formatPrice(project.price)}
                      </span>
                      <span style={styles.cardCta}>İncele →</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  page: {
    backgroundColor: "#fff",
    margin: 0,
    padding: 0,
    fontFamily: "'Georgia', 'Times New Roman', serif",
  },

  /* ── HERO ── */
  hero: {
    position: "relative",
    width: "100%",
    height: "520px",
    overflow: "hidden",
    backgroundColor: "#111",
  },
  heroImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    objectPosition: "center",
    display: "block",
    opacity: 0.72,
  },
  heroOverlay: {
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.35) 55%, rgba(0,0,0,0.05) 100%)",
  },
  heroContent: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    padding: "40px 48px",
    maxWidth: "960px",
    margin: "0 auto",
  },
  categoryBadge: {
    display: "inline-block",
    backgroundColor: "#2a9d8f",
    color: "#fff",
    fontSize: "11px",
    fontWeight: "700",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    padding: "4px 12px",
    borderRadius: "3px",
    marginBottom: "14px",
    fontFamily: "'Segoe UI', Arial, sans-serif",
  },
  heroTitle: {
    fontSize: "clamp(26px, 4vw, 42px)",
    fontWeight: "700",
    color: "#ffffff",
    lineHeight: "1.25",
    margin: "0 0 18px 0",
    textShadow: "0 2px 8px rgba(0,0,0,0.4)",
    fontFamily: "'Georgia', serif",
  },
  heroMeta: {
    display: "flex",
    gap: "24px",
    flexWrap: "wrap",
  },
  metaItem: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "13px",
    color: "rgba(255,255,255,0.82)",
    fontFamily: "'Segoe UI', Arial, sans-serif",
  },
  metaIcon: {
    width: "14px",
    height: "14px",
    flexShrink: 0,
  },

  /* ── CONTENT ── */
  contentWrapper: {
    maxWidth: "760px",
    margin: "0 auto",
    padding: "48px 24px 64px",
  },
  leadText: {
    fontSize: "20px",
    lineHeight: "1.7",
    color: "#1a1a1a",
    fontWeight: "400",
    fontFamily: "'Georgia', serif",
    borderLeft: "4px solid #2a9d8f",
    paddingLeft: "20px",
    marginBottom: "32px",
    marginTop: 0,
  },
  divider: {
    width: "60px",
    height: "3px",
    backgroundColor: "#2a9d8f",
    marginBottom: "32px",
    borderRadius: "2px",
  },
  bodyText: {
    fontSize: "18px",
    lineHeight: "1.85",
    color: "#333",
    marginBottom: "24px",
    fontFamily: "'Georgia', serif",
  },
  inlineH1: {
    fontSize: "32px",
    fontWeight: "700",
    color: "#1a1a1a",
    marginTop: "40px",
    marginBottom: "20px",
    fontFamily: "'Georgia', serif",
  },
  inlineH2: {
    fontSize: "28px",
    fontWeight: "700",
    color: "#1a1a1a",
    marginTop: "40px",
    marginBottom: "16px",
    fontFamily: "'Georgia', serif",
  },
  inlineH3: {
    fontSize: "22px",
    fontWeight: "700",
    color: "#2c3e50",
    marginTop: "32px",
    marginBottom: "12px",
    fontFamily: "'Georgia', serif",
  },
  inlineH4: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#2c3e50",
    marginTop: "24px",
    marginBottom: "10px",
    fontFamily: "'Georgia', serif",
  },
  blockquote: {
    margin: "32px 0",
    padding: "20px 28px",
    backgroundColor: "#f7f9fb",
    borderLeft: "4px solid #2a9d8f",
    borderRadius: "0 6px 6px 0",
    fontSize: "19px",
    fontStyle: "italic",
    color: "#444",
    lineHeight: "1.7",
  },
  list: {
    margin: "16px 0 24px 24px",
    padding: 0,
  },
  listItem: {
    fontSize: "18px",
    lineHeight: "1.7",
    color: "#333",
    marginBottom: "8px",
    fontFamily: "'Georgia', serif",
  },
  link: {
    color: "#2a9d8f",
    textDecoration: "none",
    borderBottom: "1px solid rgba(42,157,143,0.3)",
    transition: "border-color 0.2s ease",
  },
  contentImage: {
    maxWidth: "100%",
    height: "auto",
    borderRadius: "8px",
    margin: "24px 0",
    display: "block",
  },

  /* ── LOADING / ERROR ── */
  loading: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "60vh",
  },
  spinner: {
    width: "36px",
    height: "36px",
    border: "3px solid #e0e0e0",
    borderTop: "3px solid #2a9d8f",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },
  error: {
    padding: "100px",
    textAlign: "center",
    fontSize: "1.2rem",
    color: "#c0392b",
  },
  backButton: {
    marginTop: "20px",
    padding: "10px 24px",
    backgroundColor: "#2a9d8f",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "600",
    fontFamily: "'Segoe UI', Arial, sans-serif",
    transition: "background-color 0.2s ease",
  },

  /* ── ORTAK BÖLÜM STILLERI ── */
  sectionInner: {
    maxWidth: "1100px",
    margin: "0 auto",
  },
  sectionHeader: {
    marginBottom: "32px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
  },
  headerIconWrapper: {
    width: "32px",
    height: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#2a9d8f",
    borderRadius: "8px",
  },
  headerIcon: {
    width: "18px",
    height: "18px",
    color: "#fff",
  },
  sectionTitle: {
    fontSize: "22px",
    fontWeight: "700",
    color: "#1a1a1a",
    margin: 0,
    fontFamily: "'Georgia', serif",
  },
  titleUnderline: {
    width: "48px",
    height: "3px",
    backgroundColor: "#2a9d8f",
    borderRadius: "2px",
  },

  /* ── SON DAKİKA HABERLER ── */
  newsSection: {
    backgroundColor: "#fff8f0",
    borderTop: "1px solid #f0e6d8",
    borderBottom: "1px solid #f0e6d8",
    padding: "48px 24px",
  },
  newsList: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  newsItem: {
    display: "flex",
    alignItems: "center",
    gap: "20px",
    padding: "16px 20px",
    backgroundColor: "#fff",
    borderRadius: "12px",
    cursor: "pointer",
    transition: "all 0.25s ease",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    border: "1px solid #f0e6d8",
  },
  newsNumber: {
    fontSize: "24px",
    fontWeight: "800",
    color: "#2a9d8f",
    fontFamily: "'Segoe UI', Arial, sans-serif",
    minWidth: "40px",
    opacity: 0.7,
  },
  newsContent: {
    flex: 1,
  },
  newsItemTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#1a1a1a",
    margin: "0 0 6px 0",
    fontFamily: "'Segoe UI', Arial, sans-serif",
  },
  newsItemMeta: {
    display: "flex",
    gap: "12px",
    alignItems: "center",
  },
  newsItemDate: {
    fontSize: "11px",
    color: "#999",
    fontFamily: "'Segoe UI', Arial, sans-serif",
  },
  newsItemCategory: {
    fontSize: "10px",
    color: "#2a9d8f",
    backgroundColor: "#e8f5f2",
    padding: "2px 8px",
    borderRadius: "10px",
    fontFamily: "'Segoe UI', Arial, sans-serif",
  },
  newsArrow: {
    width: "18px",
    height: "18px",
    color: "#ccc",
    flexShrink: 0,
    transition: "transform 0.2s ease",
  },

  /* ── BENZER MAKALELER ── */
  articlesSection: {
    backgroundColor: "#f5f7f6",
    padding: "60px 24px",
  },
  similarGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: "24px",
  },

  /* ── PROJELER ── */
  projectsSection: {
    backgroundColor: "#fff",
    padding: "60px 24px",
    borderTop: "1px solid #e5e8e6",
  },
  projectsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: "24px",
  },

  /* ── ORTAK KART STILLERI ── */
  similarCard: {
    backgroundColor: "#fff",
    borderRadius: "10px",
    overflow: "hidden",
    boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
    cursor: "pointer",
    transition: "transform 0.25s ease, box-shadow 0.25s ease",
  },
  projectCard: {
    backgroundColor: "#fff",
    borderRadius: "10px",
    overflow: "hidden",
    boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
    cursor: "pointer",
    transition: "transform 0.25s ease, box-shadow 0.25s ease",
  },
  cardImageWrapper: {
    position: "relative",
    width: "100%",
    height: "160px",
    overflow: "hidden",
    backgroundColor: "#e9ecef",
  },
  cardImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
    transition: "transform 0.3s ease",
  },
  cardBadge: {
    position: "absolute",
    top: "10px",
    left: "10px",
    backgroundColor: "rgba(42,157,143,0.92)",
    color: "#fff",
    fontSize: "10px",
    fontWeight: "700",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    padding: "3px 9px",
    borderRadius: "3px",
    fontFamily: "'Segoe UI', Arial, sans-serif",
  },
  cardBody: {
    padding: "16px",
  },
  cardTitle: {
    fontSize: "15px",
    fontWeight: "700",
    color: "#1a1a1a",
    margin: "0 0 10px 0",
    lineHeight: "1.4",
    fontFamily: "'Segoe UI', Arial, sans-serif",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  cardMeta: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
    marginBottom: "12px",
  },
  cardMetaIcon: {
    width: "13px",
    height: "13px",
    color: "#2a9d8f",
    flexShrink: 0,
  },
  cardLocation: {
    fontSize: "12px",
    color: "#666",
    fontFamily: "'Segoe UI', Arial, sans-serif",
  },
  cardDate: {
    fontSize: "12px",
    color: "#666",
    fontFamily: "'Segoe UI', Arial, sans-serif",
  },
  cardFooter: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    paddingTop: "10px",
    borderTop: "1px solid #f0f0f0",
  },
  projectFooter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: "12px",
    borderTop: "1px solid #f0f0f0",
  },
  cardExcerpt: {
    fontSize: "12px",
    color: "#777",
    fontFamily: "'Segoe UI', Arial, sans-serif",
    lineHeight: "1.4",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  cardPrice: {
    fontSize: "14px",
    fontWeight: "700",
    color: "#2a9d8f",
    fontFamily: "'Segoe UI', Arial, sans-serif",
  },
  cardCta: {
    fontSize: "11px",
    color: "#2a9d8f",
    fontFamily: "'Segoe UI', Arial, sans-serif",
    fontWeight: "600",
    letterSpacing: "0.03em",
  },
};

/* Spinner animasyonu ve responsive stiller */
if (typeof document !== "undefined") {
  const style = document.createElement("style");
  style.textContent = `
    @keyframes spin { to { transform: rotate(360deg); } }
    @media (max-width: 768px) {
      .hero { height: 340px !important; }
      .heroContent { padding: 24px 20px !important; }
    }
    .news-item:hover {
      transform: translateX(5px);
      border-color: #2a9d8f !important;
    }
    .news-item:hover .news-arrow {
      transform: translateX(3px);
      color: #2a9d8f !important;
    }
    .card-image:hover {
      transform: scale(1.05);
    }
  `;
  document.head.appendChild(style);
}

export default MakaleDetay;